import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Auth } from '@aws-amplify/auth';
// Importing this module also applies the Amplify configuration that
// Auth.federatedSignIn below relies on.
import {
  COGNITO_LOGIN_FAILURE,
  CognitoLoginFailureMessage,
  POPUP_LOGIN_PROVIDER_PARAM,
} from '../../services/authService';

/**
 * Bootstrap page for the popup based login flow.
 *
 * It is opened by `authService.loginFederatedPopup()` as a top-level window and
 * starts the Amplify OAuth flow itself. Doing it here rather than opening a
 * pre-built authorize URL keeps the PKCE code verifier, the `state` value and
 * the code exchange inside one storage bucket — an iframe and its popup do not
 * share storage, so a flow started in the iframe cannot be completed in the
 * popup. See docs/popup-login-plan.md.
 *
 * Rendered outside the router and the redux providers (see App.tsx) so the
 * redirect starts immediately instead of waiting for the user-info fetch.
 */
const PopupLogin = () => {
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // An empty provider is a valid configuration, not an error: with
    // REACT_APP_AWS_CUSTOM_PROVIDER unset, Cognito omits `identity_provider`
    // and the hosted UI shows its own username/password form. Mirror
    // loginFederated, which passes '' through for exactly that case.
    const provider =
      new URLSearchParams(window.location.search).get(
        POPUP_LOGIN_PROVIDER_PARAM,
      ) ?? '';

    const reportFailure = (message: string) => {
      setError(message);
      const failure: CognitoLoginFailureMessage = {
        type: COGNITO_LOGIN_FAILURE,
        message,
      };
      (window.opener as Window | null)?.postMessage(
        failure,
        window.location.origin,
      );
    };

    // Navigates this popup to the identity provider. Nothing after this runs on
    // success — the popup comes back on the /loginCallback route.
    Auth.federatedSignIn({ customProvider: provider }).catch(() => {
      reportFailure('Could not start the login flow.');
    });
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        height: '100vh',
        padding: 2,
        textAlign: 'center',
      }}>
      {error ? (
        <Typography role="alert" color="error">
          {error}
        </Typography>
      ) : (
        <>
          <CircularProgress color="primary" />
          <Typography>Redirecting to sign-in…</Typography>
        </>
      )}
    </Box>
  );
};

export default PopupLogin;
